import { apiInterceptor } from '@/services/ApiInterceptor';
import { ChatMessage, VehicleData } from '@/types';
import { FEType } from '@/types/parts';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface AppContextType {
  // Vehicle State
  vehicleData: VehicleData | null;
  setVehicleData: (data: VehicleData | null) => void;
  clearVehicleData: () => void;

  // Chat State
  messages: ChatMessage[];
  isLoading: boolean;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  clearMessages: () => void;
  setLoading: (loading: boolean) => void;

  // Mode State
  mode: 'agentic' | 'fe';
  setMode: (mode: 'agentic' | 'fe') => void;
  feTypes: FEType[];
  setFeTypes: (types: FEType[]) => void;

  // UI State
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Vehicle State
  const [vehicleData, setVehicleDataState] = useState<VehicleData | null>(null);
  
  // Update ApiInterceptor when vehicle data changes
  useEffect(() => {
    apiInterceptor.setVehicleData(vehicleData);
  }, [vehicleData]);

  const setVehicleData = (data: VehicleData | null) => {
    setVehicleDataState(data);
  };

  const clearVehicleData = () => setVehicleDataState(null);

  // Chat State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const addMessage = (message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    setMessages(prev => [
      ...prev,
      {
        ...message,
        id: crypto.randomUUID(),
        timestamp: new Date(),
      },
    ]);
  };

  const clearMessages = () => setMessages([]);

  // Mode State
  const [mode, setMode] = useState<'agentic' | 'fe'>('agentic');
  const [feTypes, setFeTypes] = useState<FEType[]>(['components', 'repairs', 'repair_jobs']);

  // UI State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  const value = {
    // Vehicle State
    vehicleData,
    setVehicleData,
    clearVehicleData,

    // Chat State
    messages,
    isLoading,
    addMessage,
    clearMessages,
    setLoading: setIsLoading,

    // Mode State
    mode,
    setMode,
    feTypes,
    setFeTypes,

    // UI State
    isSidebarOpen,
    toggleSidebar,
    setSidebarOpen: setIsSidebarOpen,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}; 